/**
 * LogFilterBar.jsx — the collapsible "SEARCH & FILTER" strip.
 *
 * All but one field maps 1:1 to a filter the backend supports (buildLogFilter:
 * search, userId, type, from, to), so the table, stat cards and user counts
 * narrow together. A pasted ObjectId goes in SEARCH — it matches a log id or a
 * query id exactly, so there is no separate id field. SHIPPING LINE is the exception — the log API has no
 * such param (a row's lines are derived from the offers inside its verbatim
 * response), so it's applied to the loaded rows in the page and the table says
 * so while it's on. Collapsed by default; the bar reports how many filters are
 * live so a narrowed view is never mistaken for the full log set.
 *
 * There is no Search button: every change is reported straight up to the page,
 * which debounces the reload (so typing an id isn't one request per keystroke).
 */
import React from "react";
import { Select } from "antd";
import { ChevronDown, Filter, Search } from "lucide-react";
import { TYPE_FILTER_OPTIONS } from "./logUtils";

const FIELD_CLASS =
  "w-full h-10 px-3 rounded-lg border border-gray-200 text-[13px] text-gray-700 outline-none focus:border-primary";

/** Native date input — `bg-white` because Safari/Firefox tint it otherwise. */
const DATE_CLASS = `${FIELD_CLASS} bg-white cursor-pointer`;

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[11px] font-semibold tracking-wide text-gray-500 uppercase">{label}</label>
      {children}
    </div>
  );
}

function LogFilterBar({ open, onToggle, filters, onChange, userOptions, lineOptions, activeCount }) {
  const setField = (field) => (value) => onChange({ ...filters, [field]: value });

  const typeOptions = [{ value: "all", label: "All types" }, ...TYPE_FILTER_OPTIONS];

  return (
    <div className="rounded-xl border border-[#F6E3B4] bg-[#FFF8E7] overflow-hidden">
      <button type="button" onClick={onToggle} className="w-full flex items-center justify-between px-5 py-3.5 text-left">
        <span className="flex items-center gap-2.5">
          <Filter size={16} className="text-primary" />
          <span className="text-[13px] font-semibold tracking-[0.08em] text-[#8A6A12] uppercase">
            Search &amp; Filter
          </span>
          {activeCount > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-primary text-white text-[11px] font-semibold">
              {activeCount} applied
            </span>
          )}
        </span>
        <ChevronDown size={18} className={`text-[#8A6A12] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-5 pt-1 pb-5 bg-white border-t border-[#F6E3B4]">
          <div className="grid grid-cols-3 gap-4 pt-4 lg:grid-cols-2 md:grid-cols-2 sm:grid-cols-1">
            {/* One box across the table's own columns: either serial, either id,
                the route ports, the user and the log type (backend: the `search`
                param in buildLogFilter). Shipping line isn't in it — it has no
                server param, which is what its own dropdown is for. */}
            <Field label="Search">
              <span className="relative block">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  className={`${FIELD_CLASS} pl-9`}
                  placeholder="Search logs..."
                  value={filters.search}
                  onChange={(event) => setField("search")(event.target.value)}
                />
              </span>
            </Field>

            <Field label="User">
              <Select
                showSearch
                allowClear
                size="large"
                className="w-full"
                placeholder="All users"
                value={filters.userId || undefined}
                onChange={(value) => setField("userId")(value || "")}
                optionFilterProp="label"
                options={userOptions}
              />
            </Field>

            <Field label="Log type">
              <Select
                size="large"
                className="w-full"
                value={filters.type}
                onChange={setField("type")}
                options={typeOptions}
              />
            </Field>

            {/* Options are whatever lines the loaded logs actually quoted, so
                the list is empty until the first read lands — and disabled
                rather than silently empty when nothing quoted at all. */}
            <Field label="Shipping line">
              <Select
                showSearch
                allowClear
                size="large"
                className="w-full"
                placeholder={lineOptions.length ? "All shipping lines" : "No lines in the current logs"}
                disabled={!lineOptions.length && !filters.shippingLine}
                value={filters.shippingLine || undefined}
                onChange={(value) => setField("shippingLine")(value || "")}
                optionFilterProp="label"
                options={lineOptions}
              />
            </Field>

            {/* Two plain date inputs rather than one range picker — `max`/`min`
                cross-bound them so an inverted range can't be picked. */}
            <Field label="From date">
              <input
                type="date"
                className={DATE_CLASS}
                value={filters.from}
                max={filters.to || undefined}
                onChange={(event) => setField("from")(event.target.value)}
              />
            </Field>

            <Field label="To date">
              <input
                type="date"
                className={DATE_CLASS}
                value={filters.to}
                min={filters.from || undefined}
                onChange={(event) => setField("to")(event.target.value)}
              />
            </Field>
          </div>
        </div>
      )}
    </div>
  );
}

export default LogFilterBar;
